import { useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, Search } from "lucide-react";

export interface ProfileGridRow {
  id: string;
  name: string;
  typeId: string;
  typeName: string;
  /** Short status label, such as Active, Private or Shared with 2. */
  status?: string;
  updatedAt?: string;
  /** Extra searchable text, such as the profile's field values. */
  searchText?: string;
}
type SortKey = "name" | "typeName" | "status" | "updatedAt";
const columns: { key: SortKey; label: string }[] = [
  { key: "name", label: "Profile" },
  { key: "typeName", label: "Profile type" },
  { key: "status", label: "Status" },
  { key: "updatedAt", label: "Updated" },
];

/**
 * A refinable, sortable grid of profiles: keyword search, a profile-type
 * dropdown and clickable column headers. Opening a row edits the profile.
 */
export function ProfileGrid({
  rows,
  types,
  onOpen,
  actions,
  empty = "No profiles yet.",
  label = "Profiles",
  type,
  onType,
}: {
  rows: ProfileGridRow[];
  types: { id: string; name: string }[];
  onOpen(row: ProfileGridRow): void;
  actions?: ReactNode;
  empty?: string;
  label?: string;
  /** Controlled profile-type filter ("" = all types). */
  type?: string;
  onType?(type: string): void;
}) {
  const [query, setQuery] = useState(""),
    [ownType, setOwnType] = useState(""),
    [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({
      key: "name",
      descending: false,
    });
  const typeFilter = type ?? ownType;
  const setType = onType ?? setOwnType;
  const showStatus = rows.some((row) => row.status),
    showUpdated = rows.some((row) => row.updatedAt);
  const shown = useMemo(() => {
    const words = query.normalize("NFKC").toLowerCase().trim().split(/\s+/);
    return rows
      .filter((row) => !typeFilter || row.typeId === typeFilter)
      .filter((row) => {
        const text =
          `${row.name} ${row.typeName} ${row.status || ""} ${row.searchText || ""}`
            .normalize("NFKC")
            .toLowerCase();
        return words.every((word) => text.includes(word));
      })
      .sort((a, b) => {
        const order = String(a[sort.key] || "").localeCompare(
          String(b[sort.key] || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        );
        return sort.descending ? -order : order;
      });
  }, [rows, query, typeFilter, sort]);
  const visibleColumns = columns.filter(
    (column) =>
      (column.key !== "status" || showStatus) &&
      (column.key !== "updatedAt" || showUpdated),
  );
  return (
    <section className="profile-grid" aria-label={label}>
      <div className="profile-grid-toolbar">
        <label className="profile-grid-search">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            aria-label="Search profiles"
            placeholder="Search by keyword…"
            value={query}
            maxLength={100}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <select
          aria-label="Profile type"
          value={typeFilter}
          onChange={(event) => setType(event.target.value)}
        >
          <option value="">All profile types</option>
          {types.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <span className="profile-grid-count" role="status">
          {shown.length} of {rows.length}
        </span>
        {actions && <div className="profile-grid-actions">{actions}</div>}
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {visibleColumns.map((column) => {
                const active = sort.key === column.key;
                return (
                  <th
                    key={column.key}
                    aria-sort={
                      active
                        ? sort.descending
                          ? "descending"
                          : "ascending"
                        : "none"
                    }
                  >
                    <button
                      type="button"
                      className="profile-grid-sort"
                      onClick={() =>
                        setSort({
                          key: column.key,
                          descending: active ? !sort.descending : false,
                        })
                      }
                    >
                      {column.label}
                      {active &&
                        (sort.descending ? (
                          <ArrowDown size={11} />
                        ) : (
                          <ArrowUp size={11} />
                        ))}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr
                key={`${row.typeId}:${row.id}`}
                tabIndex={0}
                aria-label={`Edit ${row.name}`}
                onClick={() => onOpen(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen(row);
                  }
                }}
              >
                <td>
                  <strong>{row.name}</strong>
                </td>
                <td>{row.typeName}</td>
                {showStatus && <td>{row.status}</td>}
                {showUpdated && (
                  <td>
                    {row.updatedAt && (
                      <time dateTime={row.updatedAt}>
                        {new Date(row.updatedAt).toLocaleString()}
                      </time>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && (
          <p className="profile-grid-empty">
            {rows.length ? "No profiles match these filters." : empty}
          </p>
        )}
      </div>
    </section>
  );
}
